import assertNever from "assert-never"
import moment from "moment"
import { WebLNProvider } from "webln"

import { decrypt } from "../decrypt"
import { SendPayment } from "../webln"
import {
  API,
  EditorState,
  Message,
  PlaygroundState,
  RestAPI,
  RestEditorState,
  RestPlaygroundState,
  SocketAPI,
  SocketPlaygroundState
} from "./common.types"
import { DefaultFuturesRequestsForChannel } from "./futures.types"
import { DefaultHistoricalRequest } from "./historical.types"
import { DefaultNbaRequestsForChannel } from "./nba.types"
import { DefaultNflRequestsForChannel } from "./nfl.types"
import { DefaultSpotRequestsForChannel } from "./spot.types"

/** URLs for al APIs we offer */
export const API_URL: { [key in API]: string } = {
  spot: "wss://api.suredbits.com/exchange/v0",
  futures: "wss://api.suredbits.com/futures/v0",
  historical: "https://api.suredbits.com",
  NBA: "wss://api.suredbits.com/nba/v0",
  NFL: "https://api.suredbits.com"
}

/**
 * When interacting with websockets there are several codes we can send. These are
 * the ones we use.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
 */
const WS_CODES = {
  OPEN: 1,

  NORMAL_CLOSURE: 1000
}

/**
 * When the WebSocket connection is closed, a code is provided that explains
 * what happened. These are the ones observed during development. We log this
 * whenever the socket closes.
 */
const WS_CLOSE_EVENTS: { [key: number]: string | undefined } = {
  1000: "Normal Closure",
  1001: "Going Away",
  1002: "Protocol Error",
  1003: "Unsupported Data",
  1004: "Reserved",
  1005: "No Status Recvd",
  1006: "Abnormal Closure"
}

/** Type guard for checking if an API is socket based */

/**
 * The shape of our application state when a WebSocket based API is selected
 *
 * @todo Make illegal states her unrepresentable. This would mean having a
 * `ConnectedSocketState`, `DisconnectedSocketState`, etc. Right now
 * `activeSubscription` can be defined while `websocket` is not, which
 * doesn't make sense.
 */

/** A sample LN refund invoice we can use if the user does
 * not provide any.
 */
const SAMPLE_REFUND_INVOICE =
  "lnbc1pw80c5app58wamf585fnw5h4fagnqc4tf05mv2mvjuz3qvuhgdu9ur6psmmjvqdpl235hqurfdcs8gmmjddjkcun0vaehgctyyq58g6tswp5kutndv55jsaf3xcenz2gcqzysxqyz5vqe8clzl393uhp3m42k98h4we72ceetrsts3pj0h5w9efklltunk7hfv0g8qjv4dtelxnq0cl0l7ejs9we2kwrr5wguf3rnu8uq5q90uqq8ed279"

export const InitialEditorStates: {
  [api in API]: EditorState<api>
} = {
  historical: DefaultHistoricalRequest,
  spot: DefaultSpotRequestsForChannel(SAMPLE_REFUND_INVOICE).books,
  futures: DefaultFuturesRequestsForChannel(SAMPLE_REFUND_INVOICE).tickers,
  NBA: DefaultNbaRequestsForChannel.games,
  NFL: DefaultNflRequestsForChannel.stats
}

export const initialRestState: RestPlaygroundState<RestAPI> = {
  selectedAPI: "historical",
  pendingRequest: false,
  isWebLnModalOpen: false,
  messages: [],
  editorState: InitialEditorStates.historical
}

const initialSocketState: RestPlaygroundState<"historical"> = {
  selectedAPI: "historical",
  pendingRequest: false,
  isWebLnModalOpen: false,
  messages: [],
  editorState: InitialEditorStates.historical
}

export const initialState: PlaygroundState<
  typeof initialSocketState.selectedAPI
> = initialSocketState

export type Actions<A extends API> =
  | { type: "SET_API"; API: A }
  | { type: "CONNECT" }
  | { type: "CONNECT_COMPLETE" }
  | { type: "DISCONNECT" }
  | { type: "DISCONNECT_COMPLETE" }
  | { type: "ADD_MESSAGE" } & Message
  | { type: "CLEAR_MESSAGES" }
  | { type: "CLEAR_INVOICE" }
  | { type: "SET_SUB_INVOICE"; invoice: string }
  | { type: "SET_REFUND_INVOICE"; invoice: string }
  | { type: "SET_WS"; ws: WebSocket }
  | { type: "CLEAR_WS" }
  | { type: "SET_WEBLN"; webLn: WebLNProvider }
  | { type: "OPEN_WEBLN_INFO_MODAL" }
  | { type: "CLOSE_WEBLN_INFO_MODAL" }
  | { type: "ASKED_FOR_INVOICE" }
  | { type: "SET_ACTIVE_SUBSCRIPTION"; active: false }
  | { type: "SET_ACTIVE_SUBSCRIPTION"; active: true; uuid: string }
  | { type: "SET_EDITOR_STATE"; state: EditorState<A> }
  | { type: "SET_CURRENT_MESSAGE_TO_BE_SENT"; message: string }
  | { type: "CLEAR_EDITOR_STATE" }
  | { type: "SET_PENDING_REQUEST"; pending: boolean }

export const reducer = <A extends API>(
  state: PlaygroundState<A>,
  action: Actions<A>
): PlaygroundState<typeof state.selectedAPI> => {
  if (process.env.NODE_ENV === "development") {
    // tslint:disable-next-line: no-console
    console.log("REDUCER", action)
  }
  switch (action.type) {
    case "SET_API":
      if (action.API === "historical" || action.API === "NFL") {
        return {
          ...state,
          selectedAPI: action.API,
          editorState: InitialEditorStates[action.API]
        }
      } else if (
        action.API === "NBA" ||
        action.API === "futures" ||
        action.API === "spot"
      ) {
        return {
          ...state,
          selectedAPI: action.API,
          connection: "disconnected",
          editorState: InitialEditorStates[action.API]
        }
      }
    case "CONNECT": {
      return { ...state, connection: "connecting" }
    }
    case "CONNECT_COMPLETE": {
      return { ...state, connection: "connected" }
    }
    case "DISCONNECT": {
      return { ...state, connection: "disconnecting" }
    }
    case "DISCONNECT_COMPLETE": {
      return {
        ...state,
        connection: "disconnected",
        activeSubscription: undefined
      }
    }
    case "ADD_MESSAGE": {
      return {
        ...state,
        messages: [
          {
            text: action.text,
            kind: action.kind,
            language: action.language,
            date: action.date
          },
          ...state.messages
        ]
      }
    }
    case "CLEAR_MESSAGES": {
      return { ...state, messages: [] }
    }
    case "SET_SUB_INVOICE": {
      return { ...state, invoice: action.invoice }
    }
    case "CLEAR_INVOICE": {
      return { ...state, invoice: undefined }
    }
    case "SET_WS": {
      return { ...state, websocket: action.ws }
    }
    case "CLEAR_WS": {
      return { ...state, websocket: undefined }
    }
    case "SET_WEBLN": {
      return { ...state, webLn: action.webLn }
    }
    case "CLOSE_WEBLN_INFO_MODAL": {
      return { ...state, isWebLnModalOpen: false }
    }
    case "OPEN_WEBLN_INFO_MODAL": {
      return { ...state, isWebLnModalOpen: true }
    }
    case "SET_REFUND_INVOICE": {
      return {
        ...state,
        refundInvoice: action.invoice,
        editorState: { ...state.editorState, refundInvoice: action.invoice }
      }
    }
    case "ASKED_FOR_INVOICE": {
      return { ...state, hasAskedForInvoice: true }
    }
    case "SET_ACTIVE_SUBSCRIPTION": {
      return {
        ...state,
        activeSubscription: action.active ? action.uuid : undefined,
        invoice: action.active ? undefined : state.invoice,
        connection: action.active ? "subscribed" : "connected"
      }
    }
    case "SET_EDITOR_STATE": {
      return {
        ...state,
        editorState: action.state
      }
    }
    case "SET_CURRENT_MESSAGE_TO_BE_SENT": {
      return {
        ...state,
        currentMessageToBeSent: action.message
      }
    }
    case "CLEAR_EDITOR_STATE": {
      return {
        ...state,
        editorState: InitialEditorStates[state.selectedAPI]
      }
    }
    case "SET_PENDING_REQUEST": {
      return { ...state, pendingRequest: action.pending }
    }

    default:
      return assertNever(action)
  }
}

/** Function that enables dispatching actions to the application reducer.  */
export type SbDispatch = React.Dispatch<Actions<API>>

interface SendRestPayloadArgs {
  /** The users WebLN provider, if present */
  ln: WebLNProvider | undefined
  dispatch: SbDispatch

  /** The payload to send
   */
  payload: RestEditorState<RestAPI>
}

interface OnAPIChangeArgs {
  selectedAPI: API
  dispatch: SbDispatch
  /**
   * The selected API at any given time is selected by a dropdown menu.
   * The `onChange` prop on dropdown menus are of this type
   */
  event: React.ChangeEvent<HTMLSelectElement>
}

interface SendWsPayloadArgs {
  websocket: WebSocket | undefined
  payload: string
}

export const Actions: {
  onAPIChange: (args: OnAPIChangeArgs) => void
  onConnectButtonClick: (
    state: SocketPlaygroundState<SocketAPI>,
    dispatch: SbDispatch
  ) => void
  sendWsPayload: (state: SendWsPayloadArgs) => void
  sendRestPayload: (args: SendRestPayloadArgs) => Promise<any>
} = {
  onAPIChange: ({ dispatch, event, selectedAPI }) => {
    const { value } = event.target

    let newAPI: API | undefined

    if (value.includes("NFL")) {
      newAPI = "NFL"
    } else if (value.includes("NBA")) {
      newAPI = "NBA"
    } else if (value.includes("Spot")) {
      newAPI = "spot"
    } else if (value.includes("Future")) {
      newAPI = "futures"
    } else if (value.includes("Historical")) {
      newAPI = "historical"
    } else {
      // tslint:disable-next-line: no-console
      console.error("Unknown API value", value)
    }

    if (newAPI) {
      dispatch({ type: "SET_API", API: newAPI })
      // other APIs can only change when not
      // connected to socket, this also clears
      // state. this is not the case with
      // historical, and we need to special-case
      // the clearing
      if (selectedAPI === "historical" && newAPI !== "historical") {
        dispatch({ type: "CLEAR_MESSAGES" })
        dispatch({ type: "CLEAR_INVOICE" })
        dispatch({ type: "CLEAR_EDITOR_STATE" })
      }
    }
  },
  sendWsPayload: ({ payload, websocket }) => {
    if (websocket) {
      // tslint:disable-next-line: no-console
      console.log("Sending WS message: %O", payload)
      websocket.send(payload)
    } else {
      // tslint:disable-next-line: no-console
      console.log("WebSocket not present...")
    }
  },
  sendRestPayload: async ({ ln, dispatch, payload }) => {
    const path = RestEditorState.getPath(payload)
    const fullUrl = API_URL[payload.api] + path
    dispatch({
      type: "ADD_MESSAGE",
      text: `GET: ${fullUrl}`,
      kind: "sent",
      language: "language-http",
      date: moment()
    })

    dispatch({ type: "SET_PENDING_REQUEST", pending: true })

    try {
      const httpResponse = await fetch(fullUrl)
      const json = await httpResponse.json()

      dispatch({
        type: "ADD_MESSAGE",
        text: json,
        language: "language-json",
        kind: "received",
        date: moment()
      })

      const {
        invoice,
        encryptedData
      }: { [key: string]: string | undefined } = json

      if (invoice) {
        dispatch({ type: "SET_SUB_INVOICE", invoice })
      }

      if (ln && invoice && encryptedData) {
        const preimage = await SendPayment({ ln, invoice })

        const decrypted = decrypt(encryptedData, preimage)
        dispatch({
          type: "ADD_MESSAGE",
          date: moment(),
          language: "language-json",
          text: JSON.parse(decrypted),
          kind: "decrypted"
        })
      } else {
        // pass, no WebLN provider
        // TODO add some logging here?
      }
    } catch {
      // pass
    } finally {
      dispatch({ type: "SET_PENDING_REQUEST", pending: false })
    }
  },
  onConnectButtonClick: (args, dispatch) => {
    const { websocket, activeSubscription, selectedAPI } = args
    if (websocket) {
      if (activeSubscription) {
        const unsubMsg = JSON.stringify({
          event: "unsubscribe",
          uuid: activeSubscription
        })
        return Actions.sendWsPayload({ websocket, payload: unsubMsg })
      } else {
        dispatch({ type: "DISCONNECT" })
        return websocket.close(WS_CODES.NORMAL_CLOSURE)
      }
    } else {
      const ws = new WebSocket(API_URL[selectedAPI])
      dispatch({ type: "SET_WS", ws })

      ws.addEventListener("open", _ => {
        dispatch({ type: "CONNECT" })
      })

      ws.addEventListener("close", ({ code, reason }) => {
        const codeDesc = WS_CLOSE_EVENTS[code]
        // tslint:disable-next-line: no-console
        console.log(
          `Received WS close event. Code: ${code}${
            codeDesc ? ` (${codeDesc})` : ""
          }, reason: ${reason || "no reason"} `
        )
        dispatch({ type: "DISCONNECT_COMPLETE" })
        dispatch({ type: "CLEAR_WS" })
        dispatch({ type: "CLEAR_MESSAGES" })
        dispatch({ type: "CLEAR_INVOICE" })
        dispatch({ type: "CLEAR_EDITOR_STATE" })
      })

      ws.addEventListener("error", event => {
        // tslint:disable-next-line: no-console
        console.error("Received WS eror: %O", event)
      })

      let receivedLast100Ms = false
      setInterval(() => (receivedLast100Ms = false), 100)
      ws.addEventListener("message", ({ data }) => {
        try {
          // tslint:disable-next-line: no-console
          console.log("Received WS message: %O", JSON.parse(data))
        } catch (e) {
          // pass
        }
        const { invoice }: { invoice: string | undefined } = JSON.parse(data)
        if (invoice) {
          dispatch({ type: "SET_SUB_INVOICE", invoice })
        }

        try {
          const parsed = JSON.parse(data)
          const event: unknown = parsed.event
          if (typeof event === "string") {
            if (event === "payment received") {
              dispatch({
                type: "SET_ACTIVE_SUBSCRIPTION",
                active: true,
                uuid: parsed.uuid
              })
            } else if (event === "unsubscribed") {
              dispatch({ type: "SET_ACTIVE_SUBSCRIPTION", active: false })
            }
          }
          const message: unknown = parsed.message
          if (typeof message === "string") {
            if (message.includes("Welcome to the Suredbits")) {
              dispatch({ type: "CONNECT_COMPLETE" })
            }
          }
        } catch {
          // pass
        }

        // poor man's debouncing
        // the issue is that we need to debounce the amount of updates
        // to avoid for example bitmex book updates chrashing everything
        // but we also need to process all incoming data to look for
        // "payment received" messages or "???"
        if (!receivedLast100Ms) {
          receivedLast100Ms = true
          dispatch({
            type: "ADD_MESSAGE",
            text: JSON.parse(data),
            language: "language-json",
            kind: "received",
            date: moment()
          })
        }
      })
    }
  }
}
